/**
 * WebSocket → TanStack Query bridge for live notifications.
 *
 * Adapts what `@most/ws-client` will export. Endpoint `GET /api/v1/ws` (upgrade), authenticated
 * by the same `most_session` cookie (plans §6).
 *
 * ANONYMITY (plans §8, the 🔴 rule): a WS frame is built by the SAME per-audience DTO mapper as
 * the REST response, server-side. There are no raw domain events on the bus, and this client
 * does no filtering — filtering on the client would mean the sensitive data already reached the
 * producer's browser, where the devtools network tab is one keystroke away.
 *
 * Consequently we treat frames as INVALIDATION SIGNALS, not as data. A frame says "your
 * notifications changed"; we then re-fetch through the audited REST endpoint. Even if a future
 * frame carried a payload, nothing here would render it.
 *
 * A producer is subscribed to `user:{id}` only — never `role:admin` (asserted by ANON-W-03).
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from './queryClient';
import { chatEvents } from './chat';

/** Frame types a producer may receive on `user:{id}` (plans §6 topic table). */
type WsEventType =
  | 'notification.new'
  | 'notification.unread_count'
  | 'order.status_changed'
  | string;

interface WsFrame {
  type: WsEventType;
  // Deliberately not typed further, and deliberately never read for rendering. See the header.
  [k: string]: unknown;
}

function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/v1/ws`;
}

/**
 * Opens the socket for the lifetime of the shell and invalidates the relevant queries when the
 * server says something changed. Reconnects with capped exponential backoff; a dead socket
 * degrades the app to "refresh to see new orders", never to a broken screen.
 */
export function useNotificationSocket(enabled: boolean): void {
  const qc = useQueryClient();

  React.useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let retry = 0;
    let reconnectTimer: number | undefined;
    let disposed = false;

    const connect = () => {
      if (disposed) return;

      socket = new WebSocket(wsUrl());

      socket.onopen = () => {
        retry = 0;
      };

      socket.onmessage = (event) => {
        let frame: WsFrame;
        try {
          frame = JSON.parse(String(event.data)) as WsFrame;
        } catch {
          return; // A malformed frame is a server bug; it is not the UI's job to guess.
        }

        switch (frame.type) {
          case 'notification.new':
          case 'notification.unread_count':
            // Re-fetch through REST rather than trusting the frame's contents.
            void qc.invalidateQueries({ queryKey: qk.notifications });
            void qc.invalidateQueries({ queryKey: qk.me }); // unread badge on `Me`
            break;

          case 'order.status_changed':
            // Producers are NOT notified of status changes (docs/04 §1.3) — but if a frame does
            // arrive, refreshing the list they are already looking at is harmless and correct.
            void qc.invalidateQueries({ queryKey: ['producer', 'orders'] });
            void qc.invalidateQueries({ queryKey: ['producer', 'order'] });
            break;

          case 'chat.message':
          case 'chat.read':
            // Support chat: refetch the thread through the REST mapper, and let the widget
            // know (it may want to scroll/mark-read). Still only {type, id} — never data.
            void qc.invalidateQueries({ queryKey: qk.chatMessages });
            void qc.invalidateQueries({ queryKey: qk.chat });
            chatEvents.emit({ type: frame.type, id: typeof frame.id === 'string' ? frame.id : undefined });
            break;

          case 'call.state':
          case 'call.signal':
            // The call engine reacts to the id and refetches over REST. SDP never rides here.
            chatEvents.emit({ type: frame.type, id: typeof frame.id === 'string' ? frame.id : undefined });
            break;

          default:
            break;
        }
      };

      socket.onclose = () => {
        if (disposed) return;
        // Backoff: 1s, 2s, 4s … capped at 30s. A reconnect storm against a restarting API is
        // how you turn a 5-second deploy into a 5-minute outage.
        const delay = Math.min(1000 * 2 ** retry, 30_000);
        retry += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // `onclose` always follows; the reconnect is scheduled there.
        socket?.close();
      };
    };

    connect();

    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      // 1000 = normal closure; prevents the server logging this as an abnormal drop.
      socket?.close(1000, 'unmount');
    };
  }, [enabled, qc]);
}
